/**
 * One-time local → cloud migration ("Opslaan in de cloud"): creates the
 * project on the server, uploads every local photo that isn't already in
 * cloud storage, then syncs the full document. Built entirely on the
 * primitives in `syncClient.ts` — this module only adds the orchestration
 * and the pure decisions about *what* still needs uploading.
 *
 * Every step persists progress locally as it happens (see the
 * `saveProject`/`updateProjectMetaFields` calls threaded through the loop),
 * so an interrupted migration (tab closed, network drop) leaves the
 * project in a recoverable `pending`/`error` state rather than a half
 * migrated, unrecoverable one — calling `migrateProjectToCloud` again
 * resumes rather than restarting from scratch.
 */
import type { AssetReference, PhotobookProject } from '@fotoboek/domain';
import { assetBlobStore } from '../storage/blobStore';
import { getProjectMeta, saveProject, updateProjectMetaFields } from '../storage/projectRepository';
import {
  CloudApiError,
  completeCloudAssetUpload,
  createCloudProject,
  initCloudAssetUpload,
  pushProjectSync,
  uploadAssetBinary,
} from './syncClient';

export type MigratePhase = 'creating' | 'uploading-assets' | 'syncing' | 'done';
export type MigrateProgress = { phase: MigratePhase; assetIndex?: number; assetTotal?: number };
export type MigrateResult =
  | { status: 'ok'; project: PhotobookProject }
  | { status: 'error'; message: string; recoverable: boolean };

/** Assets that still need to be uploaded (or re-uploaded after a failed attempt). */
export function selectAssetsPendingUpload(project: PhotobookProject): AssetReference[] {
  return project.assets.filter((asset) => !asset.cloudAssetId || asset.syncStatus !== 'uploaded');
}

/** Pure: records a successful asset upload on the project document. */
export function applyUploadedAssetToProject(
  project: PhotobookProject,
  localAssetId: string,
  patch: { cloudAssetId: string; cloudObjectKey: string; thumbnailLocator?: string },
): PhotobookProject {
  return {
    ...project,
    assets: project.assets.map((asset) =>
      asset.id === localAssetId
        ? {
            ...asset,
            cloudAssetId: patch.cloudAssetId,
            cloudObjectKey: patch.cloudObjectKey,
            thumbnailLocator: patch.thumbnailLocator ?? asset.thumbnailLocator,
            syncStatus: 'uploaded' as const,
          }
        : asset,
    ),
  };
}

/** Pure: links a local project to its freshly created server counterpart. */
export function finalizeCloudLink(
  project: PhotobookProject,
  cloudProjectId: string,
): PhotobookProject {
  return { ...project, ownerMode: 'cloud', cloudProjectId };
}

function messageFor(err: unknown, fallback: string): string {
  return err instanceof CloudApiError ? err.message : fallback;
}

export async function migrateProjectToCloud(
  project: PhotobookProject,
  opts?: { onProgress?: (progress: MigrateProgress) => void; deviceLabel?: string },
): Promise<MigrateResult> {
  let working = project;
  const meta = await getProjectMeta(project.id);
  // Resolve the cloud link from either the document or the local meta record
  // — they can briefly disagree if a previous migration attempt created the
  // server project but was interrupted before the document was re-saved.
  let cloudProjectId = working.cloudProjectId ?? meta?.cloudProjectId;

  opts?.onProgress?.({ phase: 'creating' });
  if (!cloudProjectId) {
    try {
      const created = await createCloudProject({
        title: working.title,
        description: working.description,
        productProfileId: working.productProfileId,
        pageCount: working.pages.length,
      });
      cloudProjectId = created.id;
      working = finalizeCloudLink(working, cloudProjectId);
      await saveProject(working);
      await updateProjectMetaFields(working.id, {
        cloudProjectId,
        cloudRevision: created.revision,
        syncState: 'pending',
        syncError: undefined,
      });
    } catch (err) {
      const message = messageFor(err, 'Aanmaken van het cloudproject is mislukt.');
      await updateProjectMetaFields(project.id, { syncState: 'error', syncError: message });
      return { status: 'error', message, recoverable: true };
    }
  } else if (working.ownerMode !== 'cloud') {
    // The server project already exists (from an earlier, interrupted
    // attempt) but the local document was never flagged as cloud-linked —
    // finish linking it before resuming the asset upload loop below.
    working = finalizeCloudLink(working, cloudProjectId);
    await saveProject(working);
  }

  const pending = selectAssetsPendingUpload(working);
  for (let i = 0; i < pending.length; i++) {
    const asset = pending[i];
    if (!asset) continue;
    opts?.onProgress?.({
      phase: 'uploading-assets',
      assetIndex: i + 1,
      assetTotal: pending.length,
    });
    try {
      const blob = await assetBlobStore.get(working.id, asset.id, 'original');
      if (!blob) continue; // Nothing local to upload for this asset — skip instead of failing the whole migration.

      const init = await initCloudAssetUpload({
        mimeType: asset.mimeType,
        byteSize: asset.byteSize,
        checksumSha256: asset.checksumSha256,
        widthPx: asset.widthPx,
        heightPx: asset.heightPx,
      });

      if (!init.alreadyUploaded && init.uploadUrl) {
        await uploadAssetBinary(init.uploadUrl, blob, asset.mimeType);
        await completeCloudAssetUpload(init.assetId);
      }

      working = applyUploadedAssetToProject(working, asset.id, {
        cloudAssetId: init.assetId,
        cloudObjectKey: init.objectKey,
      });
      await saveProject(working);
    } catch (err) {
      const message = messageFor(err, `Uploaden van foto “${asset.originalFileName}” is mislukt.`);
      await updateProjectMetaFields(working.id, { syncState: 'error', syncError: message });
      return { status: 'error', message, recoverable: true };
    }
  }

  opts?.onProgress?.({ phase: 'syncing' });
  const pushed = await pushProjectSync(working, { deviceLabel: opts?.deviceLabel });
  if (pushed.status === 'synced') {
    opts?.onProgress?.({ phase: 'done' });
    return { status: 'ok', project: pushed.project };
  }
  if (pushed.status === 'conflict') {
    opts?.onProgress?.({ phase: 'done' });
    return { status: 'ok', project: pushed.project };
  }
  const message =
    pushed.status === 'offline'
      ? 'Geen verbinding met de server tijdens synchroniseren.'
      : pushed.status === 'error'
        ? pushed.message
        : 'Dit project kon niet worden gekoppeld aan de cloud.';
  return { status: 'error', message, recoverable: true };
}
